import React from "react";

const CardPreview = ({ cardData, size = "medium" }) => {
  const sizeConfig = {
    small: { width: "350px", height: "220px", fontSize: "10px" },
    medium: { width: "450px", height: "285px", fontSize: "12px" },
    large: { width: "550px", height: "348px", fontSize: "14px" }
  };

  const currentSize = sizeConfig[size] || sizeConfig.medium;

  // Utiliser les données de l'API complètement, afficher "null" si les valeurs sont null
  const data = cardData || {};
  
  const displayValue = (value) => {
    if (value === null || value === undefined) {
      return "null";
    }
    return value;
  };

  // Fonction pour extraire les données de bénéficiaire depuis n'importe quelle structure API
  const getBeneficiaryData = () => {
    // Si les données sont déjà dans le format attendu
    if (data.beneficiary) {
      return data.beneficiary;
    }
    
    // Sinon, mapper depuis les différents formats possibles de l'API
    return {
      matricule: data.cardNumber || data.matricule || data.beneficiaryId || data.id,
      nom: data.lastName || data.nom || data.beneficiaryName?.split(' ')[0] || data.name?.split(' ')[0],
      prenoms: data.firstName || data.prenoms || data.beneficiaryName?.split(' ').slice(1).join(' ') || data.name?.split(' ').slice(1).join(' '),
      dateNaissance: data.birthDate || data.dateNaissance || data.dateOfBirth,
      photo: data.photo || data.beneficiaryPhoto || data.image
    };
  };

  // Fonction pour extraire les données d'assuré depuis n'importe quelle structure API  
  const getAssuredData = () => {
    // Si les données sont déjà dans le format attendu
    if (data.assured) {
      return data.assured;
    }
    
    // Sinon, mapper depuis les différents formats possibles de l'API
    return {
      matricule: data.beneficiary?.matricule || data.matriculeAssure || data.assuredId,
      nomPrenoms: data.beneficiary?.fullName || data.nomPrenomsAssure || data.assuredName
    };
  };

  const beneficiaryData = getBeneficiaryData();
  const assuredData = getAssuredData();

  // Composant QR Code simplifié
  const QRCode = ({ size = "80" }) => (
    <svg width={size} height={size} viewBox="0 0 25 25" style={{ backgroundColor: "white" }}>
      <rect width="25" height="25" fill="white"/>
      {/* Pattern de QR Code simplifié */}
      <rect x="1" y="1" width="7" height="7" fill="black"/>
      <rect x="17" y="1" width="7" height="7" fill="black"/>
      <rect x="1" y="17" width="7" height="7" fill="black"/>
      <rect x="2" y="2" width="5" height="5" fill="white"/>
      <rect x="18" y="2" width="5" height="5" fill="white"/>
      <rect x="2" y="18" width="5" height="5" fill="white"/>
      <rect x="4" y="4" width="1" height="1" fill="black"/>
      <rect x="20" y="4" width="1" height="1" fill="black"/>
      <rect x="4" y="20" width="1" height="1" fill="black"/>
      {/* Motifs centraux */}
      <rect x="9" y="9" width="7" height="7" fill="black"/>
      <rect x="10" y="10" width="5" height="5" fill="white"/>
      <rect x="12" y="12" width="1" height="1" fill="black"/>
      {/* Motifs supplémentaires */}
      <rect x="9" y="1" width="1" height="1" fill="black"/>
      <rect x="11" y="1" width="1" height="1" fill="black"/>
      <rect x="13" y="1" width="1" height="1" fill="black"/>
      <rect x="15" y="1" width="1" height="1" fill="black"/>
      <rect x="1" y="9" width="1" height="1" fill="black"/>
      <rect x="1" y="11" width="1" height="1" fill="black"/>
      <rect x="1" y="13" width="1" height="1" fill="black"/>
      <rect x="1" y="15" width="1" height="1" fill="black"/>
      <rect x="23" y="9" width="1" height="1" fill="black"/>
      <rect x="23" y="11" width="1" height="1" fill="black"/>
      <rect x="23" y="13" width="1" height="1" fill="black"/>
      <rect x="23" y="15" width="1" height="1" fill="black"/>
      <rect x="9" y="23" width="1" height="1" fill="black"/>
      <rect x="11" y="23" width="1" height="1" fill="black"/>
      <rect x="13" y="23" width="1" height="1" fill="black"/>
      <rect x="15" y="23" width="1" height="1" fill="black"/>
    </svg>
  );

  return (
    <div className="d-flex justify-content-center">
      <div 
        className="position-relative shadow-lg"
        style={{
          width: currentSize.width,
          height: currentSize.height,
          backgroundColor: "white",
          borderRadius: "12px",
          overflow: "hidden",
          fontFamily: "Arial, sans-serif",
          fontSize: currentSize.fontSize
        }}
      >
        {/* En-tête bleu courbé */}
        <div 
          className="position-relative text-center d-flex align-items-center justify-content-center text-white"
          style={{
            background: "linear-gradient(135deg, #4472C4 0%, #2E4B8C 100%)",
            height: "22%",
            clipPath: "ellipse(85% 100% at 50% 0%)"
          }}
        >
          <h4 
            className="mb-0 fw-bold text-white"
            style={{ 
              fontSize: `${parseInt(currentSize.fontSize) + 6}px`,
              letterSpacing: "1px",
              textShadow: "0 1px 2px rgba(0,0,0,0.3)"
            }}
          >
            CARTE SANTÉ ACTIVA
          </h4>
        </div>

        {/* Zone principale blanche */}
        <div 
          className="px-3"
          style={{ 
            backgroundColor: "white",
            height: "78%",
            paddingTop: "8px"
          }}
        >
          <div className="d-flex h-100">
            {/* Colonne gauche - Photo et QR */}
            <div 
              className="d-flex flex-column me-3"
              style={{ width: "30%" }}
            >
              {/* Photo du bénéficiaire */}
              <div 
                className="border-2 mb-2 d-flex align-items-center justify-content-center"
                style={{ 
                  height: "65%",
                  backgroundColor: "#f8f9fa",
                  border: "2px solid #4472C4",
                  borderRadius: "4px"
                }}
              >
                {beneficiaryData?.photo && beneficiaryData.photo !== "null" ? (
                  <img 
                    src={beneficiaryData.photo} 
                    alt="Photo"
                    className="w-100 h-100"
                    style={{ objectFit: "cover", borderRadius: "2px" }}
                  />
                ) : (
                  <div className="text-center text-muted d-flex align-items-center justify-content-center" style={{ fontSize: `${parseInt(currentSize.fontSize)}px` }}>
                    {displayValue(beneficiaryData?.photo)}
                  </div>
                )}
              </div>
              
              {/* Code QR */}
              <div 
                className="d-flex align-items-center justify-content-center"
                style={{ 
                  height: "30%",
                  backgroundColor: "black",
                  borderRadius: "4px",
                  padding: "4px"
                }}
              >
                <div className="text-center">
                  <QRCode size={size === "large" ? "65" : size === "medium" ? "55" : "45"} />
                  <div 
                    className="text-white mt-1"
                    style={{ fontSize: `${parseInt(currentSize.fontSize) - 2}px`, fontWeight: "bold" }}
                  >
                    CODE QR
                  </div>
                </div>
              </div>
            </div>

            {/* Colonne droite - Informations */}
            <div style={{ width: "70%" }}>
              {/* Section Bénéficiaire */}
              <div className="mb-3">
                <h5 
                  className="fw-bold mb-2"
                  style={{ 
                    color: "#4472C4", 
                    fontSize: `${parseInt(currentSize.fontSize) + 4}px`,
                    marginBottom: "8px"
                  }}
                >
                  Bénéficiaire
                </h5>
                
                <div className="mb-1">
                  <span className="fw-normal" style={{ fontSize: currentSize.fontSize }}>
                    Matricule
                  </span>
                  <span className="fw-bold ms-4" style={{ fontSize: currentSize.fontSize }}>
                    : {displayValue(beneficiaryData?.matricule)}
                  </span>
                </div>
                
                <div className="mb-1">
                  <span className="fw-normal" style={{ fontSize: currentSize.fontSize }}>
                    Nom
                  </span>
                  <span className="fw-bold ms-5" style={{ fontSize: currentSize.fontSize }}>
                    : {displayValue(beneficiaryData?.nom)}
                  </span>
                </div>
                
                <div className="mb-1">
                  <span className="fw-normal" style={{ fontSize: currentSize.fontSize }}>
                    Prénoms
                  </span>
                  <span className="fw-bold ms-3" style={{ fontSize: currentSize.fontSize }}>
                    : {displayValue(beneficiaryData?.prenoms)}
                  </span>
                </div>
                
                <div className="mb-1">
                  <span className="fw-normal" style={{ fontSize: currentSize.fontSize }}>
                    Date de naissance
                  </span>
                  <span className="fw-bold ms-1" style={{ fontSize: currentSize.fontSize }}>
                    : {displayValue(beneficiaryData?.dateNaissance)}
                  </span>
                </div>
              </div>

              {/* Section Assuré.e */}
              <div>
                <h5 
                  className="fw-bold mb-2"
                  style={{ 
                    color: "#4472C4", 
                    fontSize: `${parseInt(currentSize.fontSize) + 4}px`,
                    marginBottom: "8px"
                  }}
                >
                  Assuré.e
                </h5>
                
                <div className="mb-1">
                  <span className="fw-normal" style={{ fontSize: currentSize.fontSize }}>
                    Matricule
                  </span>
                  <span className="fw-bold ms-4" style={{ fontSize: currentSize.fontSize }}>
                    : {displayValue(assuredData?.matricule)}
                  </span>
                </div>
                
                <div>
                  <span className="fw-normal" style={{ fontSize: currentSize.fontSize }}>
                    Nom et Prénoms
                  </span>
                  <span className="fw-bold ms-1" style={{ fontSize: currentSize.fontSize }}>
                    : {displayValue(assuredData?.nomPrenoms)}
                  </span>
                </div>
              </div>

              {/* Logo Activa en bas à droite */}
              <div 
                className="position-absolute d-flex align-items-end"
                style={{ 
                  bottom: "12px", 
                  right: "15px",
                  height: "40px"
                }}
              >
                <div className="text-end">
                  <div 
                    className="fw-bold d-flex align-items-baseline"
                    style={{ 
                      color: "#4472C4",
                      fontSize: `${parseInt(currentSize.fontSize) + 2}px`,
                      lineHeight: "1"
                    }}
                  >
                    <span className="me-1" style={{ fontSize: `${parseInt(currentSize.fontSize) + 8}px` }}>
                      Activa
                    </span>
                    <div className="d-flex flex-column" style={{ fontSize: `${parseInt(currentSize.fontSize) - 2}px` }}>
                      <span style={{ color: "#666", lineHeight: "1", marginBottom: "0" }}>
                        assurances
                      </span>
                      <span style={{ color: "#4472C4", lineHeight: "1", fontWeight: "bold" }}>
                        passionnément clients
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardPreview;